import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ListOrdered,
  Loader2,
  Map as MapIcon,
  Plus,
  Radio,
} from 'lucide-react';
import clsx from 'clsx';
import { apiClient } from '@/services/api';
import {
  useFocusLiveStream,
  useSetDevicePublication,
  useSetLiveStreamRecording,
  useTeamLiveWall,
} from '@/hooks/useTeamLiveWall';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useTeamLivePositions } from '@/hooks/useTeamLivePositions';
import { useTeamLiveTiles } from '@/hooks/useTeamLiveTiles';
import { TeamLiveTile } from '@/components/liveStream/TeamLiveTile';
import { TeamLiveDirectorActions } from '@/components/liveStream/TeamLiveDirectorActions';
import { DevicePresenceTile } from '@/components/liveStream/DevicePresenceTile';
import { ExternalStreamTile } from '@/components/liveStream/ExternalStreamTile';
import { ExternalStreamForm } from '@/components/liveStream/ExternalStreamForm';
import { TeamLiveFocusViewer } from '@/components/liveStream/TeamLiveFocusViewer';
import { TeamLiveSortableGrid, type TeamLiveTileDrag, type TeamLiveTileExtras } from '@/components/liveStream/TeamLiveSortableGrid';
import {
  addTeamExternalStream,
  loadTeamExternalStreams,
  removeTeamExternalStream,
  type TeamExternalStream,
} from '@/utils/teamLiveExternalStreams';
import TeamLiveMapPanel from '@/components/teams/TeamLiveMapPanel';
import {
  getTeamLiveGridMode,
  getTeamLiveMobileColumns,
  TEAM_LIVE_MAX_TILES,
} from '@/utils/teamLiveGrid';
import { externalTileKey, memberTileKey } from '@/utils/teamLiveTileOrder';
import type {
  LiveStreamDisplayStatus,
  TeamLiveDeviceEntry,
  TeamLiveStreamEntry,
} from '@/types';

const ACTIVE_STATUSES = new Set<LiveStreamDisplayStatus>([
  'live',
  'starting',
  'stopping',
]);

/**
 * Standalone dark multiview for a team's live streams (issue #446): one tile
 * per member stream, appearing automatically as members go live from
 * Trackcast, with director recording controls per tile and for all live cars
 * at once. Rendered outside the app Layout (see routesConfig.ts), like
 * /watch/:sessionId.
 */
export default function TeamLivePage() {
  const { teamId = '' } = useParams();
  const { t } = useTranslation('pages');
  const [showMap, setShowMap] = useState(true);
  // Viewer-local pinned external streams (YouTube) — localStorage only.
  const [externalStreams, setExternalStreams] = useState<TeamExternalStream[]>(
    () => (teamId ? loadTeamExternalStreams(teamId) : [])
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const { data: team } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => apiClient.getTeam(teamId),
    enabled: !!teamId,
  });
  const {
    data: entries,
    isLoading,
    isError,
    refetch,
  } = useTeamLiveWall(teamId);
  // Poll while the page is open (not just while the map is visible) so the
  // breadcrumb trail accumulates continuously; the hook keeps its cache so
  // reopening the map shows the last known positions instantly.
  const { data: positions, dataUpdatedAt } = useTeamLivePositions(teamId);
  const recording = useSetLiveStreamRecording(teamId);
  // Wall remote cam control (2026-09-17): director cam on/off per device.
  const camCtl = useSetDevicePublication(teamId);

  // The wall response is a discriminated union: member streams plus
  // online-but-idle devices (presence-only tiles). Streams drive every
  // control surface; devices only occupy grid slots.
  const streamEntries = useMemo(
    () =>
      (entries ?? []).filter(
        (e): e is TeamLiveStreamEntry => e.kind === 'stream'
      ),
    [entries]
  );
  const deviceEntries = useMemo(
    () =>
      (entries ?? []).filter(
        (e): e is TeamLiveDeviceEntry => e.kind === 'device'
      ),
    [entries]
  );

  const recCount = useMemo(
    () =>
      streamEntries.filter((e) => e.liveStream.deviceStatus?.recording).length,
    [streamEntries]
  );
  const streamingEntries = useMemo(
    () =>
      streamEntries.filter((e) =>
        ACTIVE_STATUSES.has(e.liveStream.displayStatus ?? 'unknown')
      ),
    [streamEntries]
  );
  // Unified tile list: stored order first, unseen tiles appended in server
  // order (members by createdAt desc, then online devices, then externals).
  // The viewer's order decides which tiles are visible at the cap.
  const { tiles, handleOrderChange, handleMoveTile } = useTeamLiveTiles(
    teamId,
    streamingEntries,
    deviceEntries,
    externalStreams
  );
  const mode = getTeamLiveGridMode(tiles.length);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const mobileColumns = getTeamLiveMobileColumns(
    isLandscape ? 'landscape' : 'portrait'
  );
  // Mobile edit mode: per-tile arrows instead of drag; leaving mobile exits.
  const [editMode, setEditMode] = useState(false);
  useEffect(() => {
    if (!isMobile) setEditMode(false);
  }, [isMobile]);
  // Mobile focus viewer, keyed by tile key so a mid-poll reindex (an earlier
  // tile vanishing) can't re-point it at the wrong stream. A key that no
  // longer resolves means the tile is gone: the render guard auto-closes.
  const [viewerKey, setViewerKey] = useState<string | null>(null);
  const viewerIndex = viewerKey
    ? tiles.findIndex((tile) => tile.key === viewerKey)
    : -1;
  const recordables = useMemo(
    () =>
      streamEntries.filter(
        (e) =>
          e.canRecord &&
          e.liveStream.recordingControlAvailable !== false &&
          ACTIVE_STATUSES.has(e.liveStream.displayStatus ?? 'unknown') &&
          !e.liveStream.recordingDesired
      ),
    [streamEntries]
  );
  const stoppables = useMemo(
    () =>
      streamEntries.filter(
        (e) =>
          e.canRecord &&
          e.liveStream.recordingControlAvailable !== false &&
          e.liveStream.recordingDesired
      ),
    [streamEntries]
  );

  const focus = useFocusLiveStream(teamId);
  // Cam-control targets: online idle devices (on) and live streams with a
  // known device (off). A device already carrying a pending declaration is
  // skipped until the intent resolves (acked or lapsed).
  const camOnTargets = useMemo(
    () =>
      deviceEntries.filter(
        (entry) => entry.canControl && !entry.publicationControl?.pending,
      ),
    [deviceEntries],
  );
  const camOffTargets = useMemo(
    () =>
      streamingEntries.filter(
        (entry) => entry.canControl && entry.liveStream.deviceId,
      ),
    [streamingEntries],
  );
  // One armed tile at a time; Esc cancels (trackcast #328).
  const [armedStreamId, setArmedStreamId] = useState<string | null>(null);
  // Viewer-local reticle marks: private feedback, instant render; the poll
  // supplies only the applied flip via entry.focus.version matching.
  const [focusMarks, setFocusMarks] = useState<
    Map<string, { x: number; y: number; sentAt: number; version: string | null }>
  >(new Map());

  useEffect(() => {
    if (!armedStreamId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setArmedStreamId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [armedStreamId]);

  // The armed tile's pill disappears when its stream stops being live —
  // disarm with it so the armed banner can't linger over a dead tile.
  useEffect(() => {
    if (!armedStreamId) return;
    const armedEntry = streamingEntries.find(
      (e) => e.liveStream.id === armedStreamId
    );
    if (!armedEntry) setArmedStreamId(null);
  }, [streamingEntries, armedStreamId]);

  useEffect(() => {
    const timer = setInterval(() => {
      setFocusMarks((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const [streamId, mark] of prev) {
          if (Date.now() - mark.sentAt > 12_000) {
            next.delete(streamId);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleFocusPoint = (entry: TeamLiveStreamEntry, x: number, y: number) => {
    setArmedStreamId(null);
    const streamId = entry.liveStream.id;
    setFocusMarks((prev) =>
      new Map(prev).set(streamId, { x, y, sentAt: Date.now(), version: null })
    );
    focus.mutate(
      { entry, x, y },
      {
        onSuccess: (projection) =>
          setFocusMarks((prev) => {
            const mark = prev.get(streamId);
            if (!mark) return prev;
            return new Map(prev).set(streamId, { ...mark, version: projection.version });
          }),
        onError: () =>
          setFocusMarks((prev) => {
            const next = new Map(prev);
            next.delete(streamId);
            return next;
          }),
      }
    );
  };

  const setRecording = (entry: TeamLiveStreamEntry, desired: boolean) => {
    recording.mutate({ entry, desired });
  };

  const handleAddExternal = (videoId: string, label?: string) => {
    setExternalStreams(addTeamExternalStream(teamId, videoId, label));
    setShowAddForm(false);
  };

  const handleRemoveExternal = (id: string) => {
    setExternalStreams(removeTeamExternalStream(teamId, id));
  };

  const openViewerByKey = (key: string) => setViewerKey(key);

  // Shared by the grid and the focus viewer: the viewer passes the same
  // renderer with an extra TeamLiveTileExtras arg (strap auto-hide). Extras
  // mark the viewer's own copy — it must keep playing (never paused) and
  // renders no expand fallback (it IS the fullscreen view).
  const renderMemberTile = (
    entry: TeamLiveStreamEntry,
    drag: TeamLiveTileDrag | null,
    extra?: TeamLiveTileExtras
  ) => {
    const mark = focusMarks.get(entry.liveStream.id) ?? null;
    const markApplied =
      !!mark?.version &&
      entry.focus?.version === mark.version &&
      !!entry.focus?.applied;
    return (
      <TeamLiveTile
        entry={entry}
        onToggleRecording={(desired) => setRecording(entry, desired)}
        isToggling={recording.isToggling(entry)}
        focusArmed={armedStreamId === entry.liveStream.id}
        onToggleFocusArm={() =>
          setArmedStreamId((current) =>
            current === entry.liveStream.id ? null : entry.liveStream.id
          )
        }
        onFocusPoint={(x, y) => handleFocusPoint(entry, x, y)}
        isFocusing={focus.isFocusing(entry)}
        focusMark={
          mark
            ? { x: mark.x, y: mark.y, sentAt: mark.sentAt, applied: markApplied }
            : null
        }
        drag={drag}
        canControl={entry.canControl}
        pendingCamOff={
          entry.publicationControl?.desiredOn === false &&
          entry.publicationControl.pending
        }
        isRequestingCam={
          entry.liveStream.deviceId
            ? camCtl.isRequesting(entry.liveStream.deviceId)
            : false
        }
        onRequestOff={
          entry.liveStream.deviceId
            ? () =>
                camCtl.mutate({
                  deviceId: entry.liveStream.deviceId as string,
                  on: false,
                })
            : undefined
        }
        // Windowing is a mobile-only concern; only the grid copy (extra
        // absent) pauses, so a single player decodes per stream and the
        // fullscreen viewer keeps playing its own.
        windowingEnabled={isMobile}
        paused={
          extra === undefined && viewerKey === memberTileKey(entry.liveStream.id)
        }
        onExpand={
          extra
            ? extra.onExpand
            : isMobile && !editMode
              ? () => openViewerByKey(memberTileKey(entry.liveStream.id))
              : undefined
        }
        strapHidden={extra?.strapHidden}
      />
    );
  };

  const renderDeviceTile = (
    entry: TeamLiveDeviceEntry,
    drag: TeamLiveTileDrag | null
  ) => (
    <DevicePresenceTile
      entry={entry}
      drag={drag}
      canControl={entry.canControl}
      pending={
        entry.publicationControl?.desiredOn === true &&
        entry.publicationControl.pending
      }
      isRequesting={camCtl.isRequesting(entry.device.id)}
      onRequestOn={() => camCtl.mutate({ deviceId: entry.device.id, on: true })}
      // Record from idle (2026-10-04 wall cam record model): the same
      // declaration with record: true — the cam-on GO LIVE also starts
      // recording.
      onRecord={() =>
        camCtl.mutate({ deviceId: entry.device.id, on: true, record: true })
      }
    />
  );

  const renderExternalTile = (
    stream: TeamExternalStream,
    drag: TeamLiveTileDrag | null,
    extra?: TeamLiveTileExtras
  ) => (
    <ExternalStreamTile
      stream={stream}
      onRemove={() => handleRemoveExternal(stream.id)}
      drag={drag}
      // Same gating as member tiles: only the grid copy pauses and expands.
      onExpand={
        extra
          ? extra.onExpand
          : isMobile && !editMode
            ? () => openViewerByKey(externalTileKey(stream.id))
            : undefined
      }
      paused={extra === undefined && viewerKey === externalTileKey(stream.id)}
      // The viewer's iframe copy lets swipe gestures through — cross-origin
      // YouTube embeds swallow every pointer event.
      interactive={extra === undefined}
    />
  );

  return (
    <div className="flex min-h-dvh w-full flex-col bg-black">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
        <div>
          <h1 className="text-base font-bold text-white">
            {team?.name ?? t('teamLive.title')}
          </h1>
          <p className="text-xs text-white/50">
            {t('teamLive.streamingCount', {
              count: streamingEntries.length,
              recording: recCount,
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/*
            Two clusters, split by a divider: controls that change what this
            viewer sees, then actions that change what the cars do. They used
            to be four look-alike buttons in a row (issue #738).
          */}
          <div className="flex items-center gap-2">
            {isMobile ? (
              <button
                type="button"
                onClick={() => setEditMode((v) => !v)}
                aria-pressed={editMode}
                className={clsx(
                  'inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-4 text-sm font-semibold',
                  editMode
                    ? 'border-sky-500/60 bg-sky-500/20 text-sky-200'
                    : 'border-white/20 text-white/80 hover:bg-white/10'
                )}
              >
                <ListOrdered className="h-4 w-4" />
                {editMode
                  ? t('teamLive.doneEditing')
                  : t('teamLive.editOrder')}
              </button>
            ) : null}
            <div className="relative">
              <button
                type="button"
                disabled={
                  streamingEntries.length + externalStreams.length >=
                  TEAM_LIVE_MAX_TILES
                }
                onClick={() => setShowAddForm((v) => !v)}
                aria-expanded={showAddForm}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-white/20 px-4 text-sm font-semibold text-white/80 hover:bg-white/10 disabled:opacity-40"
              >
                <Plus className="h-4 w-4" />
                {t('teamLive.addExternalStream')}
              </button>
              {showAddForm ? (
                <ExternalStreamForm
                  onAdd={handleAddExternal}
                  onCancel={() => setShowAddForm(false)}
                />
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setShowMap((v) => !v)}
              aria-pressed={showMap}
              className={clsx(
                'inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-4 text-sm font-semibold',
                showMap
                  ? 'border-sky-500/60 bg-sky-500/20 text-sky-200'
                  : 'border-white/20 text-white/80 hover:bg-white/10'
              )}
            >
              <MapIcon className="h-4 w-4" />
              {t('teamLive.mapToggle', 'Map')}
            </button>
          </div>

          <span aria-hidden className="h-7 w-px bg-white/15" />

          <TeamLiveDirectorActions
            recordables={recordables}
            stoppables={stoppables}
            camOnDeviceIds={camOnTargets.map((entry) => entry.device.id)}
            camOffDeviceIds={camOffTargets.map(
              (entry) => entry.liveStream.deviceId as string
            )}
            onRecordAll={() => recording.mutateAll(recordables, true)}
            onStopAll={() => recording.mutateAll(stoppables, false)}
            onCamAllOn={() =>
              camCtl.mutateAll(
                camOnTargets.map((entry) => ({ deviceId: entry.device.id })),
                true
              )
            }
            onCamAllOff={() =>
              camCtl.mutateAll(
                camOffTargets.map((entry) => ({
                  deviceId: entry.liveStream.deviceId as string,
                })),
                false
              )
            }
          />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {isLoading ? (
          <div
            role="status"
            className="flex flex-1 items-center justify-center gap-2 text-white/70"
          >
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : isError ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <AlertCircle className="h-8 w-8 text-white/60" />
            <p className="text-sm text-white/60">{t('teamLive.loadError')}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
            >
              {t('teamLive.retry')}
            </button>
          </div>
        ) : streamingEntries.length === 0 &&
          deviceEntries.length === 0 &&
          externalStreams.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white/70">
              <Radio className="h-6 w-6" />
            </div>
            <h2 className="text-base font-semibold text-white">
              {t('teamLive.empty.title')}
            </h2>
            <p className="max-w-sm text-sm text-white/60">
              {t('teamLive.empty.body')}
            </p>
          </div>
        ) : (
          <TeamLiveSortableGrid
            mode={mode}
            tiles={tiles}
            onOrderChange={handleOrderChange}
            mobile={isMobile}
            mobileColumns={mobileColumns}
            editMode={editMode}
            onMoveTile={handleMoveTile}
            renderMemberTile={renderMemberTile}
            renderDeviceTile={renderDeviceTile}
            renderExternalTile={renderExternalTile}
          />
        )}

        {showMap ? (
          <aside
            data-testid="team-live-map-aside"
            className="hidden w-80 shrink-0 border-l border-white/10 bg-[#080810] lg:block xl:w-96"
          >
            <TeamLiveMapPanel
              positions={positions ?? []}
              now={dataUpdatedAt || Date.now()}
              teamId={teamId}
            />
          </aside>
        ) : null}

        {viewerIndex >= 0 ? (
          <TeamLiveFocusViewer
            tiles={tiles}
            index={viewerIndex}
            onClose={() => setViewerKey(null)}
            onNavigate={(delta) => {
              const next =
                tiles[(viewerIndex + delta + tiles.length) % tiles.length];
              setViewerKey(next?.key ?? null);
            }}
            renderMemberTile={renderMemberTile}
            renderDeviceTile={renderDeviceTile}
            renderExternalTile={renderExternalTile}
          />
        ) : null}
      </div>
    </div>
  );
}
